// Canvas heat painter for the portal "Hot zones" layer — no React, no MUI, so
// it can be exercised in Node with a stubbed Maps API.
//
// google.maps.visualization.HeatmapLayer was REMOVED from the Maps JavaScript
// API (deprecated May 2025, gone in v3.65; the weekly channel the portal loads
// ships a constructor that throws), so the heat is painted by a small canvas
// OverlayView using the core API only — no new dependency. Same palette and
// the same absolute scale (weight = level / 9) as the native layer on phones.
//
// How it draws: intensity accumulates as alpha on an offscreen canvas (one
// radial gradient per cell), then a 256-entry lookup table turns alpha into
// the palette and the result is blitted onto the visible canvas. The canvas
// lives in the overlayLayer pane (under the markers) and moves with the map
// during a drag for free; it is repainted on zoom changes, on map `idle`, and
// when the cells change. The painted area is the viewport plus a margin so a
// short pan shows no gap before the next idle.

// Intensity stops → colour (shared with mobile-app/src/components/demand/
// DemandHeatLayer.js). Below the first stop the colour fades in from transparent.
export const STOPS = [[0.12, '#FFE27A'], [0.35, '#FFB300'], [0.60, '#FF7A1A'], [0.82, '#F1501A'], [1.0, '#B3200E']];
export const HEAT_OPACITY = 0.62;
const MAX_CANVAS_PX = 4096;

const hexRgb = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];

// 256 entries of [r, g, b, a] indexed by the intensity byte.
export const LUT = (() => {
    const lut = new Uint8ClampedArray(256 * 4);
    for (let i = 0; i < 256; i++) {
        const t = i / 255;
        let rgb;
        let a;
        if (t < STOPS[0][0]) {
            rgb = hexRgb(STOPS[0][1]);
            a = t / STOPS[0][0];
        } else {
            a = 1;
            let k = 0;
            while (k < STOPS.length - 1 && t > STOPS[k + 1][0]) k++;
            const [t0, c0] = STOPS[k];
            const [t1, c1] = STOPS[Math.min(k + 1, STOPS.length - 1)];
            const f = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
            const A = hexRgb(c0);
            const B = hexRgb(c1);
            rgb = [A[0] + (B[0] - A[0]) * f, A[1] + (B[1] - A[1]) * f, A[2] + (B[2] - A[2]) * f];
        }
        lut[i * 4] = rgb[0];
        lut[i * 4 + 1] = rgb[1];
        lut[i * 4 + 2] = rgb[2];
        lut[i * 4 + 3] = Math.round(a * 255);
    }
    return lut;
})();

export const metresPerPixel = (zoom, lat) => 156543.03392 * Math.cos((lat || 9) * Math.PI / 180) / Math.pow(2, zoom || 14);

// A ~500 m cell should blend with its neighbours at every zoom: radius ≈ 0.9
// cell widths in pixels, clamped so city zoom stays a field and street zoom
// does not become one giant blob.
export const radiusForZoom = (zoom) => Math.max(18, Math.min(90, Math.round(500 / metresPerPixel(zoom) * 0.9)));

// Pure painting step, exported for tests: accumulates the cells into the
// offscreen context, colourises the painted box and blits it to `ctx`.
// `toPx(cell)` returns {x, y} relative to the canvas or null. Returns the
// painted box in device pixels or null when nothing was drawn.
export function paintHeat({ ctx, off, w, h, dpr, r, opacity, cells, toPx, lut }) {
    const table = lut || LUT;
    off.width = Math.round(w * dpr);
    off.height = Math.round(h * dpr);
    const o = off.getContext('2d', { willReadFrequently: true });
    o.setTransform(dpr, 0, 0, dpr, 0, 0);
    o.clearRect(0, 0, w, h);
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    (cells || []).forEach((c) => {
        const p = toPx(c);
        if (!p) return;
        const x = p.x;
        const y = p.y;
        if (x < -r || y < -r || x > w + r || y > h + r) return;
        const a = Math.max(0.05, Math.min(1, 0.8 * (Number(c.weight) || 0)));
        const grad = o.createRadialGradient(x, y, 0, x, y, r);
        grad.addColorStop(0, 'rgba(0,0,0,' + a.toFixed(3) + ')');
        grad.addColorStop(0.5, 'rgba(0,0,0,' + (a * 0.3).toFixed(3) + ')');
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        o.fillStyle = grad;
        o.fillRect(x - r, y - r, 2 * r, 2 * r);
        minX = Math.min(minX, x - r); minY = Math.min(minY, y - r);
        maxX = Math.max(maxX, x + r); maxY = Math.max(maxY, y + r);
    });
    if (minX === Infinity) return null;
    // Colourise only the painted box (keeps the per-pixel loop small).
    const bx = Math.max(0, Math.floor(minX * dpr));
    const by = Math.max(0, Math.floor(minY * dpr));
    const bw = Math.min(off.width - bx, Math.ceil((maxX - minX) * dpr) + 1);
    const bh = Math.min(off.height - by, Math.ceil((maxY - minY) * dpr) + 1);
    if (bw <= 0 || bh <= 0) return null;
    const img = o.getImageData(bx, by, bw, bh);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3];
        if (!a) continue;
        const k = a * 4;
        d[i] = table[k];
        d[i + 1] = table[k + 1];
        d[i + 2] = table[k + 2];
        d[i + 3] = Math.round(table[k + 3] * opacity);
    }
    ctx.putImageData(img, bx, by);
    return { x: bx, y: by, w: bw, h: bh };
}

// Factory: needs the live `google.maps` namespace (OverlayView, LatLng, event).
export function makeHeatOverlay(g, doc, win) {
    const d = doc || document;
    const wnd = win || window;
    class HeatOverlay extends g.OverlayView {
        constructor() {
            super();
            this.canvas = d.createElement('canvas');
            this.canvas.style.position = 'absolute';
            this.canvas.style.pointerEvents = 'none';
            this.off = d.createElement('canvas');
            this.cells = [];
            this.opacity = HEAT_OPACITY;
            this.zoomDrawn = null;
            this.listeners = [];
            this.raf = 0;
            this.paints = 0;
        }
        onAdd() {
            this.getPanes().overlayLayer.appendChild(this.canvas);
            const map = this.getMap();
            if (map) this.listeners.push(g.event.addListener(map, 'idle', () => this.schedule()));
        }
        onRemove() {
            this.listeners.forEach((l) => g.event.removeListener(l));
            this.listeners = [];
            if (this.raf && wnd.cancelAnimationFrame) wnd.cancelAnimationFrame(this.raf);
            this.raf = 0;
            if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
        }
        draw() {
            // Called by the API on every map move; the pane carries the canvas
            // along, so only a zoom change needs a fresh paint here.
            const map = this.getMap();
            if (map && map.getZoom() !== this.zoomDrawn) this.schedule();
        }
        setCells(cells, opacity) {
            this.cells = cells || [];
            this.opacity = typeof opacity === 'number' ? opacity : HEAT_OPACITY;
            this.schedule();
        }
        schedule() {
            if (this.raf) return;
            const raf = wnd.requestAnimationFrame || ((fn) => setTimeout(fn, 16));
            this.raf = raf(() => {
                this.raf = 0;
                // Runs outside React's try/catch: a Maps API surprise must log,
                // never surface as an uncaught error on the Dashboard.
                try { this.repaint(); } catch (e) { console.warn('[demandHeatCanvas] repaint failed:', e && e.message); }
            });
        }
        repaint() {
            const map = this.getMap();
            const proj = this.getProjection();
            if (!map || !proj) return null;
            const bounds = map.getBounds();
            if (!bounds) return null;
            const ne = proj.fromLatLngToDivPixel(bounds.getNorthEast());
            const sw = proj.fromLatLngToDivPixel(bounds.getSouthWest());
            if (!ne || !sw) return null;
            const zoom = map.getZoom();
            const r = radiusForZoom(zoom);
            const vw = Math.abs(ne.x - sw.x);
            const vh = Math.abs(sw.y - ne.y);
            // Render scale: the device bitmap never exceeds MAX_CANVAS_PX per
            // side, so on very large viewports the scale drops below the screen
            // ratio (softer heat on a 4K wall display instead of a clipped one).
            const dpr = Math.max(0.5, Math.min(1.5, wnd.devicePixelRatio || 1,
                MAX_CANVAS_PX / Math.max(1, vw), MAX_CANVAS_PX / Math.max(1, vh)));
            // The margin is whatever survives the cap, per axis, so the viewport
            // itself is always fully covered.
            const cap = MAX_CANVAS_PX / dpr;
            const want = (v) => Math.round(v * 0.35) + r * 2;
            const w = Math.min(cap, vw + 2 * want(vw));
            const h = Math.min(cap, vh + 2 * want(vh));
            const left = Math.min(sw.x, ne.x) - Math.max(0, (w - vw) / 2);
            const top = Math.min(ne.y, sw.y) - Math.max(0, (h - vh) / 2);
            this.zoomDrawn = zoom;
            this.paints += 1;
            if (!this.cells.length) {
                // Nothing to draw: release both bitmaps instead of re-allocating
                // full-size canvases on every idle while the layer is quiet.
                this.canvas.width = 0;
                this.canvas.height = 0;
                this.off.width = 0;
                this.off.height = 0;
                return null;
            }
            this.canvas.width = Math.min(MAX_CANVAS_PX, Math.round(w * dpr));
            this.canvas.height = Math.min(MAX_CANVAS_PX, Math.round(h * dpr));
            this.canvas.style.width = w + 'px';
            this.canvas.style.height = h + 'px';
            this.canvas.style.left = left + 'px';
            this.canvas.style.top = top + 'px';
            const ctx = this.canvas.getContext('2d');
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            const toPx = (c) => {
                const p = proj.fromLatLngToDivPixel(new g.LatLng(c.latitude, c.longitude));
                return p ? { x: p.x - left, y: p.y - top } : null;
            };
            return paintHeat({ ctx, off: this.off, w, h, dpr, r, opacity: this.opacity, cells: this.cells, toPx });
        }
    }
    return new HeatOverlay();
}
